// Isolated sports-data API layer aligned to the FINALIZED GOAT contract
// (Publish 21). ALL sports URLs live here — exact Bearer-auth paths under /api/v1:
//   /sports/teams
//   /sports/conferences
//   /sports/standings
//   /sports/rankings
//   /sports/team/{id}
//   /sports/team/{id}/roster
//   /sports/team/{id}/stats
//   /sports/game/{id}/boxscore
//   /sports/game/{id}/plays
//   /sports/game/{id}/odds
//
// Legacy /games endpoints are used ONLY as a harmless backward-compatible
// fallback for a team's schedule IF /sports/team/{id} intentionally omits it —
// never as a substitute that would mask a /sports error.

import { api, AuthExpiredError } from "./client";
import { normalizeTeam, teamGamesFromFeed, computeRecordFromGames, normalizePlay, flattenTeamsPayload, flattenStandingsGroups, normalizeContractSchedule, boxscoreRows, normalizePlayerResponse, normalizePlayersResponse } from "../lib/sports";

export const SPORTS_ENDPOINTS = {
  teams: (league, season) => `/sports/teams${qs({ league, season })}`,
  conferences: (league) => `/sports/conferences${qs({ league })}`,
  standings: (league, season) => `/sports/standings${qs({ league, season })}`,
  rankings: (league, season) => `/sports/rankings${qs({ league, season })}`,
  team: (id) => `/sports/team/${encodeURIComponent(id)}`,
  teamRoster: (id, season) => `/sports/team/${encodeURIComponent(id)}/roster${qs({ season })}`,
  teamStats: (id, season) => `/sports/team/${encodeURIComponent(id)}/stats${qs({ season })}`,
  gameBoxscore: (id) => `/sports/game/${encodeURIComponent(id)}/boxscore`,
  gamePlays: (id, skip = 0, limit = 50) => `/sports/game/${encodeURIComponent(id)}/plays${qs({ limit, skip })}`,
  gameOdds: (id) => `/sports/game/${encodeURIComponent(id)}/odds`,
  player: (id, season) => `/sports/player/${encodeURIComponent(id)}${qs({ season })}`,
  players: (p = {}) => `/sports/players${qs({ q: p.q, league: p.league, team_id: p.teamId, season: p.season, limit: p.limit, cursor: p.cursor })}`,
  // Legacy fallback (schedule derivation only):
  legacyGames: () => "/games",
};

function qs(params) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

// Real network/timeout → "error"; any other failure (e.g. not-yet-deployed → 404)
// → "feature_unavailable". Never silently swallows a /sports error into legacy data.
async function safe(promise) {
  try {
    return { ok: true, data: await promise };
  } catch (e) {
    if (e instanceof AuthExpiredError) throw e;
    const msg = String(e?.message || "");
    return { ok: false, state: /network|timed out/i.test(msg) ? "error" : "feature_unavailable" };
  }
}

function latestTimestamp(list, keys = ["source_timestamp", "fetched_at"]) {
  const ts = (list || []).map((x) => keys.map((k) => x?.[k]).find(Boolean)).filter(Boolean).sort();
  return ts.length ? ts[ts.length - 1] : null;
}

export const Sports = {
  async teams(league, season) {
    const r = await safe(api(SPORTS_ENDPOINTS.teams(league, season), { auth: true }));
    if (!r.ok) return { state: r.state, teams: [] };
    // /sports/teams is the canonical provider feed — flatten the nested
    // conferences→divisions→teams shape; every team here is a provider team.
    const teams = flattenTeamsPayload(r.data).map(normalizeTeam).filter(Boolean).filter((t) => !league || !t.league || t.league === league);
    return { state: teams.length ? "ok" : "empty", teams, source_timestamp: r.data.last_updated || r.data.source_timestamp || latestTimestamp(teams), stale: !!r.data.stale };
  },

  async conferences(league) {
    const r = await safe(api(SPORTS_ENDPOINTS.conferences(league), { auth: true }));
    if (!r.ok) return { state: r.state, conferences: [] };
    const list = r.data.conferences || r.data.items || [];
    return { state: list.length ? "ok" : "empty", conferences: list, source_timestamp: r.data.source_timestamp || null };
  },

  async standings(league, season) {
    const r = await safe(api(SPORTS_ENDPOINTS.standings(league, season), { auth: true }));
    if (!r.ok) return { state: r.state, groups: [] };
    const d = r.data || {};
    const groups = flattenStandingsGroups(d.groups || d.standings || []);
    const has = groups.some((g) => (g.teams || []).length);
    return { state: has ? "ok" : "empty", groups, season: d.season || season, source_timestamp: d.last_updated || d.source_timestamp || latestTimestamp(groups), coverage: d.coverage_note || d.coverage || null };
  },

  async rankings(league, season) {
    const r = await safe(api(SPORTS_ENDPOINTS.rankings(league, season), { auth: true }));
    if (!r.ok) return { state: r.state, poll: null, teams: [] };
    const d = r.data || {};
    const teams = d.rankings || d.teams || [];
    return { state: teams.length ? "ok" : "empty", poll: d.poll || null, week: d.week ?? null, coverage: d.coverage_note || null, teams, source_timestamp: d.last_updated || d.source_timestamp || null };
  },

  // Team overview via exact /sports/team/{id}. Returns normalized team + schedule
  // when the contract includes it. Falls back to /teams list only if this 404s.
  async team(id) {
    const r = await safe(api(SPORTS_ENDPOINTS.team(id), { auth: true }));
    if (r.ok && r.data) {
      const raw = r.data.team || r.data;
      return {
        state: "ok",
        team: normalizeTeam(raw),
        schedule: normalizeContractSchedule(r.data.schedule || r.data.games || raw.schedule),
        record: raw.record || r.data.record || null,
        source_timestamp: raw.source_timestamp || r.data.source_timestamp || null,
      };
    }
    // Backward-compatible fallback (team identity only) if /sports not yet live.
    const list = await safe(api(SPORTS_ENDPOINTS.teams(), { auth: true }));
    if (!list.ok) return { state: list.state, team: null };
    const raw = flattenTeamsPayload(list.data).find((t) => t.id === id);
    if (!raw) return { state: "not_found", team: null };
    return { state: "ok", team: normalizeTeam(raw), schedule: null, record: null, source_timestamp: raw.source_timestamp || raw.fetched_at || null, fallback: true };
  },

  async roster(id, season) {
    const r = await safe(api(SPORTS_ENDPOINTS.teamRoster(id, season), { auth: true }));
    if (!r.ok) return { state: r.state, players: [] };
    const d = r.data || {};
    const players = d.players || d.roster || d.leaders || [];
    return { state: players.length ? "ok" : "empty", players, source_timestamp: d.source_timestamp || null };
  },

  async teamStats(id, season) {
    const r = await safe(api(SPORTS_ENDPOINTS.teamStats(id, season), { auth: true }));
    if (!r.ok) return { state: r.state, stats: null, players: [] };
    const d = r.data || {};
    const stats = d.team_stats || d.stats || d.team || null;
    const players = d.player_stats || d.players || d.leaders || [];
    return { state: (stats || players.length) ? "ok" : "empty", stats, players, source_timestamp: d.last_updated || d.source_timestamp || null };
  },

  // Box/team/player stats via exact /sports/game/{id}/boxscore. Production shape
  // is { team_stats:[{team_abbr, stats:{k:v}}], player_stats:[...] }.
  async gameBoxscore(id) {
    const r = await safe(api(SPORTS_ENDPOINTS.gameBoxscore(id), { auth: true }));
    if (!r.ok) return { state: r.state, teams: [], rows: [], players: [] };
    const d = r.data || {};
    const box = d.boxscore || d.box_score || d;
    const teamStats = box.team_stats || (Array.isArray(box.stats) ? box.stats : []);
    const { teams, rows } = boxscoreRows(teamStats);
    return { state: rows.length ? "ok" : "empty", teams, rows, players: d.player_stats || box.player_stats || d.players || [], source_timestamp: d.last_updated || d.source_timestamp || null };
  },

  async gamePlays(id, skip = 0, limit = 50) {
    const r = await safe(api(SPORTS_ENDPOINTS.gamePlays(id, skip, limit), { auth: true }));
    if (!r.ok) return { state: r.state, plays: [], total: 0 };
    const d = r.data || {};
    const plays = (d.plays || d.play_by_play || d.events || []).map(normalizePlay);
    return { state: plays.length ? "ok" : "empty", plays, total: d.total ?? d.count ?? plays.length, skip: d.skip ?? skip, limit: d.limit ?? limit };
  },

  async gameOdds(id) {
    const r = await safe(api(SPORTS_ENDPOINTS.gameOdds(id), { auth: true }));
    if (!r.ok) return { state: r.state, snapshots: [] };
    const d = r.data || {};
    const snapshots = d.odds || d.snapshots || d.markets || d.lines || [];
    return { state: snapshots.length ? "ok" : "empty", snapshots, informational_only: !!d.informational_only, unavailable_reason: d.unavailable_reason || null, source_timestamp: d.last_updated || d.source_timestamp || null };
  },

  // Current-season players directory (search + filters + cursor pagination).
  async playersDirectory(params = {}) {
    try {
      const data = await api(SPORTS_ENDPOINTS.players(params), { auth: true });
      return normalizePlayersResponse(data);
    } catch (e) {
      if (e instanceof AuthExpiredError) throw e;
      const msg = String(e?.message || "").toLowerCase();
      const state = /network|timed out/.test(msg) ? "error" : "feature_unavailable";
      return { state, players: [], nextCursor: null, season: params.season ?? null, coverage: null };
    }
  },

  // Current-season player detail via exact /sports/player/{id}. Distinguishes
  // not_found (404) / network error / feature_unavailable so the page can be
  // honest. Never falls back to fabricated data.
  async player(id, season) {
    try {
      const data = await api(SPORTS_ENDPOINTS.player(id, season), { auth: true });
      return normalizePlayerResponse(data, season);
    } catch (e) {
      if (e instanceof AuthExpiredError) throw e;
      const msg = String(e?.message || "").toLowerCase();
      const state = /not found|404|no such|unknown player/.test(msg) ? "not_found"
        : /network|timed out/.test(msg) ? "error" : "feature_unavailable";
      return { state, player: null, season: season ?? null, seasonStats: null, gameLog: [], coverage: null };
    }
  },

  // Backward-compatible fallback: derive a team's schedule/results + record from
  // the legacy /games feed. Used ONLY when /sports/team/{id} omits schedule.
  async teamGames(teamId) {
    const r = await safe(api(SPORTS_ENDPOINTS.legacyGames(), { auth: true }));
    if (!r.ok) return { state: r.state, games: [], record: null };
    const games = teamGamesFromFeed(r.data.games || [], teamId);
    return { state: games.length ? "ok" : "empty", games, record: computeRecordFromGames(games) };
  },
};
